"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { trpc } from "@calcom/trpc/react";
import { showToast } from "@calcom/ui/components/toast";

import {
  getPushRegistration,
  getPushSupport,
  requestNotificationPermission,
  showClientTestNotification,
  type PushSupport,
  urlB64ToUint8Array,
} from "../lib/web-push";

interface WebPushContextProps {
  permission: NotificationPermission;
  isLoading: boolean;
  isSubscribed: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  testNotification: () => Promise<void>;
  isSupported: boolean;
  supportReason?: string;
}

export const WebPushContext = createContext<WebPushContextProps | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export function WebPushProvider({ children }: ProviderProps) {
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "denied"
  );
  const [pushManager, setPushManager] = useState<PushManager | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [support, setSupport] = useState<PushSupport>({ supported: false });

  const { mutateAsync: addSubscription } =
    trpc.viewer.loggedInViewerRouter.addNotificationsSubscription.useMutation();
  const { mutateAsync: removeSubscription } =
    trpc.viewer.loggedInViewerRouter.removeNotificationsSubscription.useMutation();

  useEffect(() => {
    const nextSupport = getPushSupport();
    setSupport(nextSupport);
    if (!nextSupport.supported) return;

    getPushRegistration()
      .then(async (registration) => {
        if ("pushManager" in registration) {
          setPushManager(registration.pushManager);
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        }
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  }, []);

  const contextValue = useMemo(
    () => ({
      permission,
      isLoading,
      isSubscribed,
      isSupported: support.supported,
      supportReason: support.reason,
      subscribe: async () => {
        try {
          setIsLoading(true);
          if (!support.supported || !pushManager) {
            showToast(
              support.reason ||
                "Browser notifications are unavailable here. Use Chrome, Edge, or Firefox on desktop, or install this site to your Home Screen on iPhone/iPad.",
              "error"
            );
            return;
          }
          const newPermission = await requestNotificationPermission();
          setPermission(newPermission);

          if (newPermission === "granted") {
            const subscription = await pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlB64ToUint8Array(process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY || ""),
            });
            await addSubscription({ subscription: JSON.stringify(subscription) });
            setIsSubscribed(true);
            showToast("Notifications enabled successfully", "success");
          } else if (newPermission === "denied") {
            showToast("Notifications are blocked for this site. Allow them in your browser site settings and try again.", "error");
          }
        } catch (error) {
          console.error("Failed to subscribe:", error);
          if (
            error instanceof DOMException &&
            error.name === "InvalidAccessError" &&
            error.message.includes("applicationServerKey")
          ) {
            showToast(
              "This browser rejected the push configuration. Try Chrome, Edge, or Firefox on desktop, or install this site as a Home Screen app on iPhone/iPad.",
              "error"
            );
          } else if (error instanceof DOMException && error.name === "NotAllowedError") {
            showToast("Notifications are blocked for this site. Allow them in your browser site settings and try again.", "error");
          } else {
            showToast("Failed to enable notifications", "error");
          }
        } finally {
          setIsLoading(false);
        }
      },
      unsubscribe: async () => {
        if (!pushManager) return;
        try {
          setIsLoading(true);
          const subscription = await pushManager.getSubscription();
          if (subscription) {
            const subscriptionJson = JSON.stringify(subscription);
            await subscription.unsubscribe();
            await removeSubscription({ subscription: subscriptionJson });
            setIsSubscribed(false);
            showToast("Notifications disabled successfully", "success");
          }
        } catch (error) {
          console.error("Failed to unsubscribe:", error);
          showToast("Failed to disable notifications", "error");
        } finally {
          setIsLoading(false);
        }
      },
      testNotification: async () => {
        if (!isSubscribed || permission !== "granted") {
          showToast("Enable notifications before sending a test.", "error");
          return;
        }
        try {
          setIsLoading(true);
          await showClientTestNotification({
            title: "Cal.diy notifications are ready",
            body: "This is a local browser test. Booking updates will appear here too.",
            url: "/bookings",
            tag: "cal-diy-push-test",
            actions: [{ action: "open-bookings", title: "Open bookings" }],
          });
          showToast("Test notification sent.", "success");
        } catch (error) {
          console.error("Failed to send test notification:", error);
          showToast(error instanceof Error ? error.message : "Failed to send test notification.", "error");
        } finally {
          setIsLoading(false);
        }
      },
    }),
    [permission, isLoading, isSubscribed, support, pushManager, addSubscription, removeSubscription]
  );

  return <WebPushContext.Provider value={contextValue}>{children}</WebPushContext.Provider>;
}
